import { useEffect, useState } from 'react';
import type { Operaciones, ParDeTokens } from '../../api/operaciones';
import type { CuponDelCatalogo, FichaDeNegocio, PartnerDePlataforma } from '../../api/ops/backoffice';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Campo } from '../../componentes/Campo';
import { Desplegable } from '../../componentes/Desplegable';
import { Icono } from '../../componentes/Icono';
import { Segmentos } from '../../componentes/Segmentos';
import { Ventana } from '../../componentes/Ventana';
import { errorDeCampo } from '../../logica/acceso';
import {
    DIAS_PRUEBA,
    ERROR_CODIGO,
    ERROR_CODIGO_CADUCADO,
    AVISO_DATOS_PERSONALES,
    ERROR_MOTIVO,
    TEXTO_ENTRAR,
    codigoValido,
    lineaPartner,
    motivoValido,
    notaEntrar,
    partnersQueCoinciden,
    siglas,
    textoDeError,
    textoPrueba,
    textosAtribuir,
    textosQuitarPartner,
} from './logica';

/**
 * Ventanas de la ficha del negocio (1.20, RV Ventana): entrar en solo lectura o suplantar (G68 a G70, G186), ampliar la
 * prueba, aplicar un cupón, atribuir o cambiar el partner (G235) y quitarlo (G236). Todas piden motivo (de 10 a 500
 * letras, queda en Auditoría); el error del motivo sale al intentar guardar, como en el prototipo.
 */
export type TipoVentana = 'entrar' | 'prueba' | 'cupon' | 'partner' | 'quitar-partner';

interface PropsVentana {
    ops: Operaciones;
    ficha: FichaDeNegocio;
    movil: boolean;
    superadmin: boolean;
    /** Nombre o email del propietario (ver `lineasResumen`). */
    propietario: string;
    finPrueba: string | null;
    onCerrar: () => void;
    /** Hecho: la ficha y la lista se vuelven a pedir. */
    onHecho: () => void;
    onAccesoAbierto: (tokens: ParDeTokens) => void;
}

/** Motivo, «lo intentó» y ocupado, comunes a todas las ventanas. */
function useFormulario() {
    const [motivo, setMotivo] = useState('');
    const [intentado, setIntentado] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [errorMotivo, setErrorMotivo] = useState<string | null>(null);
    const error = errorMotivo ?? (intentado && !motivoValido(motivo) ? ERROR_MOTIVO : null);
    return {
        motivo,
        setMotivo: (v: string) => {
            setMotivo(v);
            setErrorMotivo(null);
        },
        intentado,
        setIntentado,
        ocupado,
        setOcupado,
        errorMotivo: error,
        setErrorMotivo,
    };
}

function CampoMotivo({ f, ayuda, onEnter }: { f: ReturnType<typeof useFormulario>; ayuda: string; onEnter: () => void }) {
    return <Campo etiqueta="Motivo" valor={f.motivo} onChange={f.setMotivo} error={f.errorMotivo} ayuda={ayuda} max={500} onEnter={onEnter} />;
}

export function Ventanas({ tipo, ...p }: PropsVentana & { tipo: TipoVentana | null }) {
    // Cada ventana se monta al abrirla: sus campos empiezan vacíos.
    return (
        <>
            {tipo === 'entrar' && <VentanaEntrar {...p} />}
            {tipo === 'prueba' && <VentanaPrueba {...p} />}
            {tipo === 'cupon' && <VentanaCupon {...p} />}
            {tipo === 'partner' && <VentanaPartner {...p} />}
            {tipo === 'quitar-partner' && <VentanaQuitarPartner {...p} />}
        </>
    );
}

/** Un `422` con el error en `reason` va bajo el motivo; el resto, en un aviso breve. */
function useTratarError(f: ReturnType<typeof useFormulario>) {
    const avisar = useAvisoBreve();
    return (e: unknown) => {
        f.setOcupado(false);
        const delMotivo = errorDeCampo(e, 'reason');
        if (delMotivo !== null) {
            f.setErrorMotivo(delMotivo);
            return;
        }
        avisar({ tipo: 'error', texto: textoDeError(e) });
    };
}

/**
 * **Entrar en <negocio>** (1.20): con motivo, 60 minutos y aviso al propietario. Soporte, solo lectura (G70). El
 * Superadmin elige «Solo lectura» o «Suplantar» al propietario (`owner_member_id`, G137) con el código de dos pasos del
 * momento (G186: «Vale 5 min»). Con el acceso abierto, el panel entra en el negocio con sus tokens.
 */
function VentanaEntrar({ ops, ficha, movil, superadmin, propietario, onCerrar, onAccesoAbierto }: PropsVentana) {
    const f = useFormulario();
    const tratar = useTratarError(f);
    const [modo, setModo] = useState(0);
    const [codigo, setCodigo] = useState('');
    const [codigoCaducado, setCodigoCaducado] = useState(false);
    const suplantar = superadmin && modo === 1;
    const errorCodigo = suplantar && f.intentado ? (!codigoValido(codigo) ? ERROR_CODIGO : codigoCaducado ? ERROR_CODIGO_CADUCADO : null) : null;

    const entrar = () => {
        if (f.ocupado) {
            return;
        }
        f.setIntentado(true);
        if (!motivoValido(f.motivo) || (suplantar && !codigoValido(codigo))) {
            return;
        }
        f.setOcupado(true);
        setCodigoCaducado(false);
        const reason = f.motivo.trim();
        ops.openPlatformAccess(
            ficha.id,
            suplantar ? { mode: 'impersonation', impersonated_member_id: ficha.owner_member_id, reason, two_factor_code: codigo } : { mode: 'read', reason },
        )
            .then((abierto) => {
                onAccesoAbierto(abierto.tokens);
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && (e.codigo === 'recent_verification_required' || e.codigo === 'code_invalid')) {
                    f.setOcupado(false);
                    setCodigoCaducado(true);
                    return;
                }
                tratar(e);
            });
    };

    return (
        <Ventana
            abierta
            titulo={`Entrar en ${ficha.name}`}
            texto={TEXTO_ENTRAR}
            accion={suplantar ? 'Suplantar' : 'Entrar'}
            accionIcono={suplantar ? 'user-cog' : 'arrow-right'}
            ocupado={f.ocupado}
            ocupadoTexto="Entrando…"
            movil={movil}
            onAccion={entrar}
            onCerrar={onCerrar}
        >
            {superadmin && (
                <Segmentos
                    items={[
                        { etiqueta: 'Solo lectura', icono: 'eye' },
                        { etiqueta: 'Suplantar', icono: 'user-cog' },
                    ]}
                    valor={modo}
                    movil={movil}
                    etiqueta="Cómo entrar"
                    onCambio={(i) => {
                        setModo(i);
                        f.setIntentado(false);
                    }}
                />
            )}
            <span className="rv-bo-ventana-nota">{notaEntrar(suplantar, superadmin, propietario)}</span>
            <CampoMotivo f={f} ayuda="Lo ve el propietario en su Actividad." onEnter={entrar} />
            {/* Provisional hasta C.62: el aviso de no escribir datos personales en el motivo (G1127). */}
            <span className="rv-bo-ventana-aviso">
                <Icono nombre="shield-alert" />
                {AVISO_DATOS_PERSONALES}
            </span>
            {suplantar && (
                <Campo
                    etiqueta="Código de dos pasos"
                    valor={codigo}
                    onChange={(v) => {
                        setCodigo(v.replace(/\D/g, '').slice(0, 6));
                        setCodigoCaducado(false);
                    }}
                    modo="numeric"
                    autocompletar="one-time-code"
                    max={6}
                    error={errorCodigo}
                    ayuda="Se pide al suplantar. Vale 5 min."
                    onEnter={entrar}
                />
            )}
        </Ventana>
    );
}

/** **Ampliar la prueba** (solo Superadmin, `extendTrial`): 7, 14 o 30 días, con motivo. */
function VentanaPrueba({ ops, ficha, movil, finPrueba, onCerrar, onHecho }: PropsVentana) {
    const f = useFormulario();
    const tratar = useTratarError(f);
    const [dias, setDias] = useState(0);
    const ampliar = () => {
        if (f.ocupado) {
            return;
        }
        f.setIntentado(true);
        if (!motivoValido(f.motivo)) {
            return;
        }
        f.setOcupado(true);
        ops.extendTrial(ficha.id, { days: DIAS_PRUEBA[dias] ?? 7, reason: f.motivo.trim() })
            .then(() => {
                onHecho();
            })
            .catch(tratar);
    };
    return (
        <Ventana
            abierta
            titulo="Ampliar la prueba"
            texto={textoPrueba(ficha.name, finPrueba, new Date())}
            accion="Ampliar"
            accionIcono="calendar-plus"
            ocupado={f.ocupado}
            movil={movil}
            onAccion={ampliar}
            onCerrar={onCerrar}
        >
            <Segmentos items={DIAS_PRUEBA.map((d) => ({ etiqueta: `${String(d)} días`, icono: 'calendar-plus' }))} valor={dias} movil={movil} etiqueta="Días que se añaden" onCambio={setDias} />
            <CampoMotivo f={f} ayuda="Obligatorio. Queda en Auditoría." onEnter={ampliar} />
        </Ventana>
    );
}

/** **Aplicar un cupón** del catálogo (solo Superadmin, `listCatalogCoupons` y `applyCoupon`), con motivo. */
function VentanaCupon({ ops, ficha, movil, onCerrar, onHecho }: PropsVentana) {
    const f = useFormulario();
    const tratar = useTratarError(f);
    const avisar = useAvisoBreve();
    const [cupones, setCupones] = useState<CuponDelCatalogo[] | null>(null);
    const [elegido, setElegido] = useState('');
    useEffect(() => {
        let vivo = true;
        ops.listCatalogCoupons()
            .then((r) => {
                if (vivo) {
                    setCupones(r.data);
                    setElegido((e) => (e === '' ? (r.data[0]?.code ?? '') : e));
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setCupones([]);
                    avisar({ tipo: 'error', texto: textoDeError(e, 'No se han podido cargar los cupones. Prueba de nuevo.') });
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, avisar]);
    const aplicar = () => {
        if (f.ocupado) {
            return;
        }
        f.setIntentado(true);
        if (!motivoValido(f.motivo) || elegido === '') {
            return;
        }
        f.setOcupado(true);
        ops.applyCoupon(ficha.id, { coupon_code: elegido, reason: f.motivo.trim() })
            .then(() => {
                onHecho();
            })
            .catch(tratar);
    };
    return (
        <Ventana
            abierta
            titulo="Aplicar un cupón"
            texto="Se descuenta desde la próxima factura."
            accion="Aplicar"
            accionIcono="percent"
            ocupado={f.ocupado}
            inactiva={cupones === null || cupones.length === 0}
            movil={movil}
            onAccion={aplicar}
            onCerrar={onCerrar}
        >
            <Desplegable
                etiqueta="Cupón del catálogo"
                valor={elegido}
                opciones={(cupones ?? []).map((c) => ({ valor: c.code, texto: `${c.name} · −${String(c.percent_off)} %` }))}
                onChange={setElegido}
                error={f.intentado && cupones !== null && cupones.length === 0 ? 'No hay cupones en el catálogo.' : null}
            />
            <CampoMotivo f={f} ayuda="Obligatorio. Queda en Auditoría." onEnter={aplicar} />
        </Ventana>
    );
}

/**
 * **Atribuir a un partner** o **cambiar de partner** (solo Superadmin, `listPartners` aprobados y `setPartnerAttribution`):
 * buscador por nombre o código, lista para elegir, qué cambia (G235: desde hoy) y motivo. «Atribuir» no sale hasta elegir
 * partner y escribir el motivo (desviación del prototipo: RV Ventana no tiene botón desactivado).
 */
function VentanaPartner({ ops, ficha, movil, onCerrar, onHecho }: PropsVentana) {
    const f = useFormulario();
    const tratar = useTratarError(f);
    const avisar = useAvisoBreve();
    const [partners, setPartners] = useState<PartnerDePlataforma[] | null>(null);
    const [q, setQ] = useState('');
    const [elegido, setElegido] = useState<PartnerDePlataforma | null>(null);
    useEffect(() => {
        let vivo = true;
        ops.listPartners({ status: 'approved', limit: 100 })
            .then((r) => {
                if (vivo) {
                    setPartners(r.data);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setPartners([]);
                    avisar({ tipo: 'error', texto: textoDeError(e, 'No se han podido cargar los partners. Prueba de nuevo.') });
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, avisar]);
    const t = textosAtribuir(ficha.name, ficha.partner_name, ficha.partner_attribution_source === 'manual', elegido?.legal_name ?? null);
    const lista = partnersQueCoinciden(partners ?? [], ficha.partner_id ?? null, q);
    const atribuir = () => {
        if (f.ocupado) {
            return;
        }
        f.setIntentado(true);
        if (!motivoValido(f.motivo) || elegido === null) {
            return;
        }
        f.setOcupado(true);
        ops.setPartnerAttribution(ficha.id, { partner_id: elegido.id, reason: f.motivo.trim() })
            .then(() => {
                onHecho();
            })
            .catch(tratar);
    };
    return (
        <Ventana
            abierta
            titulo={t.titulo}
            texto={t.texto}
            accion={elegido === null || f.motivo.trim() === '' ? '' : t.accion}
            accionIcono="handshake"
            ocupado={f.ocupado}
            movil={movil}
            onAccion={atribuir}
            onCerrar={onCerrar}
        >
            <Campo etiqueta="Buscar un partner" tipo="search" valor={q} onChange={setQ} ayuda="Por nombre o código." max={100} />
            <div role="radiogroup" aria-label="Partner" className="rv-bo-partners">
                {lista.map((p) => {
                    const on = elegido?.id === p.id;
                    return (
                        <button
                            key={p.id}
                            type="button"
                            role="radio"
                            aria-checked={on}
                            className="rv-bo-partner"
                            onClick={() => {
                                setElegido(p);
                            }}
                        >
                            <span className="rv-bo-siglas">{siglas(p.legal_name)}</span>
                            <span className="rv-bo-partner__textos">
                                <strong>{p.legal_name}</strong>
                                <span>{lineaPartner(p)}</span>
                            </span>
                            {on && <Icono nombre="circle-check" />}
                        </button>
                    );
                })}
                {partners !== null && lista.length === 0 && <span className="rv-bo-ninguno">Ningún partner aprobado con ese nombre o código.</span>}
            </div>
            <span className="rv-bo-efecto">{t.efecto}</span>
            <CampoMotivo f={f} ayuda="Queda en Auditoría y lo ve el partner." onEnter={atribuir} />
        </Ventana>
    );
}

/** **Quitar el partner** (solo Superadmin, G236: `partner_id` nulo, con motivo; el historial queda en Auditoría, G261). */
function VentanaQuitarPartner({ ops, ficha, movil, onCerrar, onHecho }: PropsVentana) {
    const f = useFormulario();
    const tratar = useTratarError(f);
    const t = textosQuitarPartner(ficha.name, ficha.partner_name ?? '');
    const quitar = () => {
        if (f.ocupado) {
            return;
        }
        f.setIntentado(true);
        if (!motivoValido(f.motivo)) {
            return;
        }
        f.setOcupado(true);
        ops.setPartnerAttribution(ficha.id, { partner_id: null, reason: f.motivo.trim() })
            .then(() => {
                onHecho();
            })
            .catch(tratar);
    };
    return (
        <Ventana abierta titulo={t.titulo} texto={t.texto} accion="Quitar el partner" accionIcono="unlink" ocupado={f.ocupado} movil={movil} onAccion={quitar} onCerrar={onCerrar}>
            <CampoMotivo f={f} ayuda="Obligatorio. Queda en Auditoría." onEnter={quitar} />
        </Ventana>
    );
}
